import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useNotifications } from '../../hooks/useNotifications'
import { usePreferences } from '../../hooks/usePreferences'
import type { Notification, NotificationSeverity } from '../../types'

interface Props {
  orgId: string | null
  userId: string | null
}

const SEVERITY_COLORS: Record<NotificationSeverity, string> = {
  info: 'border-l-info',
  warning: 'border-l-warning',
  critical: 'border-l-error',
}

const SEVERITY_DOTS: Record<NotificationSeverity, string> = {
  info: 'bg-info',
  warning: 'bg-warning',
  critical: 'bg-error',
}

function notificationLink(n: Notification): string | null {
  if (!n.source_type || !n.source_id) return null
  if (n.source_type === 'health' || n.source_type === 'camera_event') {
    return `/viewer?camera=${n.source_id}`
  }
  return null
}

export function NotificationBell({ orgId, userId }: Props) {
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications(orgId, userId)
  const { formatDate } = usePreferences()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  // Close on outside click
  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative text-gray-500 hover:text-gray-900 transition-colors p-1"
        title="Notifications"
      >
        {/* Bell icon */}
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-error text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
            <span className="text-xs font-semibold text-gray-900">Notifications</span>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-[10px] text-brand hover:underline"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-gray-400">
                No notifications yet
              </div>
            ) : (
              notifications.map(n => {
                const link = notificationLink(n)
                return (
                  <button
                    key={n.id}
                    onClick={() => {
                      if (!n.read_at) markRead(n.id)
                      if (link) { setOpen(false); navigate(link) }
                    }}
                    className={`w-full text-left px-3 py-2.5 border-l-2 transition-colors hover:bg-gray-50 ${
                      SEVERITY_COLORS[n.severity]
                    } ${!n.read_at ? 'bg-brand/5' : ''}`}
                  >
                    <div className="flex items-start gap-2">
                      {!n.read_at && (
                        <div className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${SEVERITY_DOTS[n.severity]}`} />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className={`text-xs ${!n.read_at ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                          {n.title}
                          {link && <span className="text-brand ml-1">&rarr;</span>}
                        </p>
                        {n.body && (
                          <p className="text-[10px] text-gray-500 mt-0.5 line-clamp-2">{n.body}</p>
                        )}
                        <p className="text-[10px] text-gray-400 mt-1">{formatDate(n.created_at)}</p>
                      </div>
                    </div>
                  </button>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}
